import PanelTitleWithShortCut from 'ui-component/RecordView/RecordViewCore/components/PanelTitleWithShortCut';
import { LogReportMode, PanelTitle } from '../utils';
import { Box } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import { SearchBar } from 'ui-component/SearchBar';
import { MouseEvent, useContext, useEffect, useRef, useState } from 'react';
import { HeaderMenu } from './components/HeaderMenu';
import { LogReportType } from 'ui-component/RecordView/types';
import { Button } from '@mui/material';
import { ExpandMore, Menu } from '@mui/icons-material';
import { LogReportHeaderButtons } from '../LogReportPanel/components/LogReportHeaderButtons';
import { LogReportHeaderMenu } from '../LogReportPanel/components/LogReportHeaderMenu';
import { PanelFormsContext } from 'contexts/panelFormsContext';

function getPanelTitle(changePanel: PanelTitle, logReportMode: LogReportMode, selectedObjectToEdit: { id: number; name: string } | null) {
    if (changePanel === PanelTitle.LogReport) {
        return `Audit Log - ${logReportMode}`;
    }
    if ([PanelTitle.ViewObject, PanelTitle.EditObject].includes(changePanel)) {
        return selectedObjectToEdit?.name ?? 'Object';
    }
    return 'Details';
}

interface PanelHeaderProps {
    changePanel: PanelTitle;
    logReportMode: LogReportMode;
    selectedObjectToEdit: { id: number; name: string } | null;
    toggleEditForm: () => void;
    onChangeSelection: (newSelection: LogReportMode) => void;
    setButtonTypeLog?: (newSelection: LogReportType) => void;
    buttonTypeLog: LogReportType;
    toggleLogReport?: () => void;
    isCreate?: boolean;
    onClickHeader?: () => void;
    rightHandPanelWidth: number;
    recordTypeId: number;
}
export const PanelHeader = ({
    changePanel,
    logReportMode,
    selectedObjectToEdit,
    toggleEditForm,
    isCreate,
    onClickHeader,
    rightHandPanelWidth,
    recordTypeId,
    ...logReportSelectorProps
}: PanelHeaderProps) => {
    const theme = useTheme();
    const [logReportHeaderMenuAnchor, setLogReportHeaderMenuAnchor] = useState<HTMLElement | null>(null);
    const { setSearchInput, searchInput } = useContext(PanelFormsContext);
    const prevChangePanel = useRef<PanelTitle>(changePanel);

    const panelTitle = getPanelTitle(changePanel, logReportMode, selectedObjectToEdit);
    const isObjectPanel = [PanelTitle.EditObject, PanelTitle.ViewObject].includes(changePanel);

    const handleOpen = (e: MouseEvent<HTMLButtonElement>) => {
        setLogReportHeaderMenuAnchor(e.currentTarget);
    };
    const handleClose = () => {
        setLogReportHeaderMenuAnchor(null);
        logReportSelectorProps?.toggleLogReport?.();
    };
    const handleChangeSelection = (newSelection: LogReportMode) => () => {
        logReportSelectorProps.onChangeSelection(newSelection);
        setLogReportHeaderMenuAnchor(null);
    };

    useEffect(() => {
        if (searchInput) {
            const curIsObjectPanel = changePanel.toLowerCase().includes('object');
            const prevIsObjectPanel = prevChangePanel.current.toLowerCase().includes('object');

            if (searchInput && curIsObjectPanel !== prevIsObjectPanel && changePanel !== prevChangePanel.current) {
                setSearchInput?.('');
                prevChangePanel.current = changePanel;
            }
        }
    }, [searchInput, changePanel, setSearchInput]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <PanelTitleWithShortCut
                shortCutAction={toggleEditForm}
                title={panelTitle ?? ''}
                id="titleId"
                showShortCuts={!changePanel.toLowerCase().includes('edit') && changePanel !== PanelTitle.LogReport}
                useBreakcrumb={isObjectPanel ? ['Details', panelTitle ?? ''] : undefined}
                onClickHeader={onClickHeader}
                rightHandPanelWidth={rightHandPanelWidth}
            />
            {!isCreate && (
                <>
                    {changePanel === PanelTitle.LogReport && (
                        <LogReportHeaderButtons
                            buttonTypeLog={logReportSelectorProps.buttonTypeLog}
                            setButtonTypeLog={logReportSelectorProps.setButtonTypeLog}
                            handleClose={handleClose}
                        />
                    )}

                    <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center', mb: '0.5rem', pl: '0.875rem', pr: '0.5rem' }}>
                        <SearchBar
                            searchInput={searchInput}
                            placeholderShouldIncludekeys
                            fullWidth
                            InputProps={{
                                sx: {
                                    bgcolor: theme.palette.primary[300],
                                    height: '2rem',
                                    '&.MuiFilledInput-input::placeholder': { color: '#C6C4C4' },
                                    '&.MuiFilledInput-input': {
                                        '&:focus': {
                                            color: theme.palette.secondary.light
                                        }
                                    }
                                }
                            }}
                            onChange={(e) => setSearchInput?.(e.target.value || '')}
                            hideKeys
                            placeholder={changePanel === PanelTitle.Edit ? 'Search' : 'Find in current page'}
                            clearSearch={() => setSearchInput?.('')}
                        />
                        {changePanel === PanelTitle.LogReport ? (
                            <>
                                <Button
                                    size="medium"
                                    color="primary"
                                    onClick={handleOpen}
                                    sx={{
                                        color: theme.palette.primary[400],
                                        backgroundColor: theme.palette.background.paper,
                                        fontWeight: 600,
                                        borderRadius: '30px',
                                        '&:hover': { color: 'red', backgroundColor: 'white' }
                                    }}
                                >
                                    <Menu fontSize="medium" />
                                    <ExpandMore fontSize="medium" />
                                </Button>
                                <LogReportHeaderMenu
                                    anchorRef={logReportHeaderMenuAnchor}
                                    onClose={handleClose}
                                    onChangeSelection={handleChangeSelection}
                                />
                            </>
                        ) : (
                            <HeaderMenu recordTypeId={recordTypeId} isObjectPanel={isObjectPanel} />
                        )}
                    </Box>
                </>
            )}
        </Box>
    );
};
